import { formatUomName } from "@/lib/uom";
import type { DecimalPrecision } from "@/utils/decimal";
import type { SalesOrder } from "@/types/salesDocuments";
import {
  formatCurrency,
  formatTotalCurrency,
  formatDate,
  formatDateTime,
  getImageDataUrl,
  getPrintStyles,
  printHtml,
} from "./printHelpers";

interface SalesOrderPrintData {
  /** Company decimal precision. */
  precision: DecimalPrecision | number;
  salesOrder: SalesOrder;
  companyInfo: {
    businessName: string;
    addressLine0: string;
    addressLine1?: string;
    addressLine2?: string;
    cityName: string;
    postalZone: string;
    state: string;
    country: string;
    contactNumber: string;
    email: string;
    tin: string;
    registrationNumber: string;
    companyLogo?: string;
    reportFooter?: string;
  };
}

const getStatusClass = (status: string): string => {
  const statusMap: Record<string, string> = {
    Draft: "status-draft",
    Confirmed: "status-confirmed",
    Processing: "status-processing",
    "Partially Delivered": "status-processing",
    Delivered: "status-delivered",
    Cancelled: "status-cancelled",
  };
  return statusMap[status] || "status-draft";
};

const getStatusLabel = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const generateSalesOrderHtml = async (
  data: SalesOrderPrintData
): Promise<string> => {
  const { salesOrder, companyInfo, precision } = data;

  // Get company logo if available
  let logoDataUrl = "";
  if (companyInfo.companyLogo) {
    try {
      logoDataUrl = await getImageDataUrl(
        `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/${companyInfo.companyLogo}`
      );
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  }

  // Build company address
  const companyAddress = [
    companyInfo.addressLine0,
    companyInfo.addressLine1,
    companyInfo.addressLine2,
    companyInfo.postalZone,
    companyInfo.cityName,
    companyInfo.state,
    companyInfo.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Build buyer address
  const buyerAddress = [
    salesOrder.buyerAddressLine0,
    salesOrder.buyerAddressLine1,
    salesOrder.buyerAddressLine2,
    salesOrder.buyerPostcode,
    salesOrder.buyerCity,
    salesOrder.buyerState,
    salesOrder.buyerCountry,
  ]
    .filter(Boolean)
    .join(", ");

  // Generate line items HTML
  const lineItemsHtml = salesOrder.lineItems
    .map(
      (item, index) => `
    <tr>
      <td class="text-center">${index + 1}</td>
      <td>${item.itemCode || ""}</td>
      <td>
        <strong>${item.description}</strong>
        ${item.classification ? `<br/><small>Class: ${item.classification}</small>` : ""}
      </td>
      <td class="text-center">${item.quantity}</td>
      <td class="text-center">${item.deliveredQuantity || 0}</td>
      <td class="text-center">${item.remainingQuantity || item.quantity}</td>
      <td class="text-center">${formatUomName(item.measurement) || "Unit"}</td>
      <td class="text-right">${formatCurrency(item.unitPrice, salesOrder.currencyCode, precision)}</td>
      <td class="text-right">${(item.discountRate ?? 0) > 0 ? item.discountRate + "%" : "-"}</td>
      <td class="text-right">${item.taxRate > 0 ? item.taxRate + "%" : "-"}</td>
      <td class="text-right">${formatCurrency(item.taxAmount, salesOrder.currencyCode, precision)}</td>
      <td class="text-right"><strong>${formatCurrency(item.total, salesOrder.currencyCode, precision)}</strong></td>
    </tr>
  `
    )
    .join("");

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Sales Order ${salesOrder.codeNumber}</title>
      ${getPrintStyles()}
    </head>
    <body>
      <div class="container">
        <!-- Header -->
        <div class="header">
          <div class="company-info">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="company-logo"/>` : ""}
            <div class="company-name">${companyInfo.businessName}</div>
            <div class="company-details">
              <div>${companyAddress}</div>
              <div>Tel: ${companyInfo.contactNumber} | Email: ${companyInfo.email}</div>
              <div>TIN: ${companyInfo.tin} | Reg No: ${companyInfo.registrationNumber}</div>
            </div>
          </div>
          <div class="document-title">
            <div class="document-type">SALES ORDER</div>
            <div class="document-number">${salesOrder.codeNumber}</div>
            <div class="document-date">Order Date: ${formatDate(salesOrder.orderDate)}</div>
            ${salesOrder.expectedDeliveryDate ? `<div class="document-date">Expected Delivery: ${formatDate(salesOrder.expectedDeliveryDate)}</div>` : ""}
            ${salesOrder.quotationNumber ? `<div class="document-date">Ref Quotation: ${salesOrder.quotationNumber}</div>` : ""}
            ${salesOrder.customerPONumber ? `<div class="document-date">Customer PO: ${salesOrder.customerPONumber}</div>` : ""}
            <div class="status-badge ${getStatusClass(salesOrder.status)}">
              ${getStatusLabel(salesOrder.status)}
            </div>
          </div>
        </div>

        <!-- Buyer Information -->
        <div class="parties-section">
          <div class="party-box">
            <div class="party-title">CUSTOMER INFORMATION</div>
            <div class="party-details">
              <div><strong>Name:</strong> ${salesOrder.buyerName}</div>
              ${salesOrder.buyerTin ? `<div><strong>TIN:</strong> ${salesOrder.buyerTin}</div>` : ""}
              ${salesOrder.buyerRegistrationNumber ? `<div><strong>Reg No:</strong> ${salesOrder.buyerRegistrationNumber}</div>` : ""}
              <div><strong>Address:</strong> ${buyerAddress}</div>
              ${salesOrder.buyerContact ? `<div><strong>Tel:</strong> ${salesOrder.buyerContact}</div>` : ""}
              ${salesOrder.buyerEmail ? `<div><strong>Email:</strong> ${salesOrder.buyerEmail}</div>` : ""}
            </div>
          </div>
        </div>

        <!-- Line Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th style="width: 80px;">Item Code</th>
              <th>Description</th>
              <th style="width: 50px;">Ordered</th>
              <th style="width: 60px;">Delivered</th>
              <th style="width: 60px;">Remaining</th>
              <th style="width: 50px;">UOM</th>
              <th style="width: 75px;">Unit Price</th>
              <th style="width: 55px;">Disc %</th>
              <th style="width: 55px;">Tax %</th>
              <th style="width: 75px;">Tax Amt</th>
              <th style="width: 85px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHtml}
          </tbody>
        </table>

        <!-- Totals Section -->
        <div class="totals-section">
          <table class="totals-table">
            <tr>
              <td>Subtotal (Excl. Tax):</td>
              <td>${formatTotalCurrency(salesOrder.totalExcludingTax, salesOrder.currencyCode)}</td>
            </tr>
            <tr>
              <td>Total Tax Amount:</td>
              <td>${formatTotalCurrency(salesOrder.totalTaxAmount, salesOrder.currencyCode)}</td>
            </tr>
            <tr>
              <td>Total (Incl. Tax):</td>
              <td>${formatTotalCurrency(salesOrder.totalIncludingTax, salesOrder.currencyCode)}</td>
            </tr>
            <tr class="total-row">
              <td>TOTAL PAYABLE:</td>
              <td>${formatTotalCurrency(salesOrder.totalPayableAmount, salesOrder.currencyCode)}</td>
            </tr>
          </table>
        </div>

        <!-- Terms and Conditions -->
        ${
          salesOrder.termsAndConditions
            ? `
        <div class="footer-section">
          <div class="footer-title">Terms and Conditions</div>
          <div class="footer-content">${salesOrder.termsAndConditions}</div>
        </div>
        `
            : ""
        }

        <!-- Remarks -->
        ${
          salesOrder.remarks
            ? `
        <div class="footer-section">
          <div class="footer-title">Remarks</div>
          <div class="footer-content">${salesOrder.remarks}</div>
        </div>
        `
            : ""
        }

        <!-- Report Footer -->
        ${
          companyInfo.reportFooter
            ? `
        <div class="footer-section">
          <div class="footer-content">${companyInfo.reportFooter}</div>
        </div>
        `
            : ""
        }

        <!-- Footer Note -->
        <div class="footer-section" style="margin-top: 30px; font-size: 8pt; color: #6b7280; text-align: center;">
          <p>This is a computer-generated document. No signature is required.</p>
          <p>Generated on ${formatDateTime(new Date())}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};

export const printSalesOrder = async (data: SalesOrderPrintData) => {
  const html = await generateSalesOrderHtml(data);
  printHtml(html, `SalesOrder_${data.salesOrder.codeNumber}`);
};
